/**
 * "Needs a look" (Phase A, A10, 32.9) — at the top of the case page, and only
 * when something needs the officer.
 *
 * **The server's list, drawn as it comes.** Each line says what the app noticed
 * and what to do; the list is worked out from the record every time the case is
 * read, and a line goes when the record stops giving a reason for it. There is
 * no "dismiss" here: a warning an officer can wave away is a hidden one (rule 3).
 *
 * Each line carries where its act lives — his particulars on this page, Papers
 * on the file, or Something has come in — and the control takes the officer
 * there. Nothing here decides anything.
 */

import { Link } from 'react-router-dom'
import type { CaseDetail, NeedsALookLine } from '../../api/client'
import './case-page.css'

export function NeedsALook({ data, readOnly }: { data: CaseDetail; readOnly: boolean }) {
  const lines = data.needs_a_look ?? []
  if (lines.length === 0) return null
  return (
    <section className="needs-a-look" data-testid="needs-a-look" aria-label="Needs a look">
      <h3>
        Needs a look <span className="cnt mono">{lines.length}</span>
      </h3>
      <ul>
        {lines.map((line) => (
          <li key={line.key} data-testid={`needs-${line.key}`}>
            <span className="says">{line.says}</span>
            <span className="to-do">{line.to_do}</span>
            {readOnly ? null : (
              <span className="act">
                <Act data={data} line={line} />
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Where the line's act lives. A plain anchor for the two on this page — his
 *  particulars open their own form on the hash — and a link for the papers. */
function Act({ data, line }: { data: CaseDetail; line: NeedsALookLine }) {
  switch (line.where) {
    case 'particulars':
      return (
        <a className="btn ghost" href={`#particular-${line.field ?? 'name'}`}>
          Correct his particulars
        </a>
      )
    case 'papers':
      return (
        <Link className="btn ghost" to={`/cases/${data.id}/papers`}>
          Papers on the file
        </Link>
      )
    case 'came-in':
      return (
        <a
          className="btn ghost"
          href="#something-came-in"
          onClick={(event) => {
            // "Something has come in" is drawn by its own component; the
            // anchor scrolls to it by its test id, which is its stable name.
            const target = document.querySelector('[data-testid="something-came-in"]')
            if (target) {
              event.preventDefault()
              target.scrollIntoView({ block: 'center', behavior: 'smooth' })
            }
          }}
        >
          Something has come in
        </a>
      )
    default:
      return null
  }
}
